import { z } from 'zod';
import { E164PhoneSchema, TimestampSchema, UuidSchema } from './common';

export const MessageStatusSchema = z.enum(['queued', 'sent', 'delivered', 'read', 'failed']);
export type MessageStatus = z.infer<typeof MessageStatusSchema>;

export const MessageEventSchema = z.object({
  id: UuidSchema,
  messageId: UuidSchema,
  status: MessageStatusSchema,
  errorCode: z.string().optional(),
  errorMessage: z.string().optional(),
  occurredAt: TimestampSchema,
});
export type MessageEvent = z.infer<typeof MessageEventSchema>;

/** Status do phone number na connection. `active` = ainda no array
 * `phoneNumberIds`. `inactive` = removido (mensagem foi enviada antes da
 * remoção). `unknown` = mensagem antiga sem `fromPhoneNumberId` registrado. */
export const PhoneNumberStatusSchema = z.enum(['active', 'inactive', 'unknown']);
export type PhoneNumberStatus = z.infer<typeof PhoneNumberStatusSchema>;

export const MessageSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  connectionId: UuidSchema,
  /** Phone number ID Meta usado pra enviar (null pra queued/antigas). */
  fromPhoneNumberId: z.string().nullable().optional(),
  /**
   * Número de telefone legível do `fromPhoneNumberId` (ex.: "+55 47 9986-3289").
   * Resolvido server-side a partir dos snapshots de WABA das Business Managers
   * do tenant. Ausente quando a mensagem ainda não saiu (`fromPhoneNumberId`
   * nulo) ou quando o ID não consta em nenhum snapshot conhecido.
   */
  fromPhoneNumberDisplay: z.string().optional(),
  /** Status do phone derivado server-side a cada GET. */
  fromPhoneNumberStatus: PhoneNumberStatusSchema.optional(),
  /** Campanha que originou a mensagem (`campaigns.id`). */
  campaignId: UuidSchema.optional(),
  /**
   * Nome da campanha (`campaigns.name`), resolvido via LEFT JOIN no service.
   * Ausente quando a mensagem não veio de campanha (cadência, inbox, teste) ou
   * quando a campanha foi removida (`campaignId` vira null por ON DELETE SET NULL).
   */
  campaignName: z.string().optional(),
  templateId: UuidSchema.optional(),
  recipientPhone: E164PhoneSchema,
  recipientName: z.string().optional(),
  templateVariables: z.record(z.string()).optional(),
  status: MessageStatusSchema,
  metaMessageId: z.string().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Message = z.infer<typeof MessageSchema>;

export const SendMessageInputSchema = z.object({
  connectionId: UuidSchema,
  templateId: UuidSchema,
  recipientPhone: E164PhoneSchema,
  recipientName: z.string().optional(),
  templateVariables: z.record(z.string()).optional(),
});
export type SendMessageInput = z.infer<typeof SendMessageInputSchema>;

export const MessageWithEventsSchema = MessageSchema.extend({
  events: z.array(MessageEventSchema),
});
export type MessageWithEvents = z.infer<typeof MessageWithEventsSchema>;

export const MessagesListQuerySchema = z.object({
  status: MessageStatusSchema.optional(),
  connectionId: UuidSchema.optional(),
  templateId: UuidSchema.optional(),
  /** Filtra mensagens de uma campanha específica (`messages.campaignId`). Usado
   * pelo card "Mensagens recentes" do detalhe da campanha e pelo deep-link
   * `/messages?campaign=<id>`. */
  campaignId: UuidSchema.optional(),
  /** Filtra mensagens com `createdAt >= dateFrom` (ISO 8601). "Desde quando"
   * do histórico — usado pelos presets de período da tela /messages. */
  dateFrom: z.string().datetime().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type MessagesListQuery = z.infer<typeof MessagesListQuerySchema>;

export const MessagesListResponseSchema = z.object({
  messages: z.array(MessageSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type MessagesListResponse = z.infer<typeof MessagesListResponseSchema>;

// ─── Endpoints atalho (DX-friendly) ─────────────────────────────────────
//
// Cada schema é body de um POST /api/v1/connections/:id/send/{type}.
// `replyToMetaMessageId` (opcional em todos) vira context.message_id no Meta.
// Todos passam pela queue de envio (governança preservada).

export const ShortcutReplySchema = z.object({
  replyToMetaMessageId: z.string().optional(),
});

export const SendTextShortcutSchema = z.object({
  to: E164PhoneSchema,
  body: z.string().min(1).max(4096),
  replyToMetaMessageId: z.string().optional(),
});
export type SendTextShortcutInput = z.infer<typeof SendTextShortcutSchema>;

export const SendTemplateShortcutSchema = z.object({
  to: E164PhoneSchema,
  /** ID local do template aprovado (rows de `templates`). */
  templateId: UuidSchema,
  recipientName: z.string().max(200).optional(),
  variables: z.record(z.string()).optional(),
  replyToMetaMessageId: z.string().optional(),
});
export type SendTemplateShortcutInput = z.infer<typeof SendTemplateShortcutSchema>;

export const SendAudioShortcutSchema = z.object({
  to: E164PhoneSchema,
  mediaId: z.string().min(1),
  replyToMetaMessageId: z.string().optional(),
});
export type SendAudioShortcutInput = z.infer<typeof SendAudioShortcutSchema>;

const XOR_MEDIA_MSG = 'Forneça mediaId OU link (exatamente um)';

export const SendImageShortcutSchema = z
  .object({
    to: E164PhoneSchema,
    mediaId: z.string().min(1).optional(),
    link: z.string().url().optional(),
    caption: z.string().max(1024).optional(),
    replyToMetaMessageId: z.string().optional(),
  })
  .refine((d) => Boolean(d.mediaId) !== Boolean(d.link), { message: XOR_MEDIA_MSG });
export type SendImageShortcutInput = z.infer<typeof SendImageShortcutSchema>;

export const SendVideoShortcutSchema = z
  .object({
    to: E164PhoneSchema,
    mediaId: z.string().min(1).optional(),
    link: z.string().url().optional(),
    caption: z.string().max(1024).optional(),
    replyToMetaMessageId: z.string().optional(),
  })
  .refine((d) => Boolean(d.mediaId) !== Boolean(d.link), { message: XOR_MEDIA_MSG });
export type SendVideoShortcutInput = z.infer<typeof SendVideoShortcutSchema>;

export const SendDocumentShortcutSchema = z
  .object({
    to: E164PhoneSchema,
    mediaId: z.string().min(1).optional(),
    link: z.string().url().optional(),
    caption: z.string().max(1024).optional(),
    filename: z.string().max(255).optional(),
    replyToMetaMessageId: z.string().optional(),
  })
  .refine((d) => Boolean(d.mediaId) !== Boolean(d.link), { message: XOR_MEDIA_MSG });
export type SendDocumentShortcutInput = z.infer<typeof SendDocumentShortcutSchema>;

export const SendLocationShortcutSchema = z.object({
  to: E164PhoneSchema,
  latitude: z.number().gte(-90).lte(90),
  longitude: z.number().gte(-180).lte(180),
  name: z.string().max(200).optional(),
  address: z.string().max(500).optional(),
  replyToMetaMessageId: z.string().optional(),
});
export type SendLocationShortcutInput = z.infer<typeof SendLocationShortcutSchema>;

export const SendContactShortcutSchema = z.object({
  to: E164PhoneSchema,
  /** Array no shape Meta `contacts`. Mantemos record genérico. */
  contacts: z.array(z.record(z.unknown())).min(1).max(10),
  replyToMetaMessageId: z.string().optional(),
});
export type SendContactShortcutInput = z.infer<typeof SendContactShortcutSchema>;

export const SendInteractiveShortcutSchema = z.object({
  to: E164PhoneSchema,
  /** Objeto `interactive` no shape Meta (button / list / cta_url). */
  interactive: z.record(z.unknown()),
  replyToMetaMessageId: z.string().optional(),
});
export type SendInteractiveShortcutInput = z.infer<typeof SendInteractiveShortcutSchema>;
